import { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
  useGetUserQuery,
  useUpdateUserAccountMutation,
  useGetSignInQuery
} from '../../services/portal';
import DismissibleAlert from '../AlertDismissible/DismissibleAlert';

const EditUserAccount = ({ userId }) => {
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('info');
  const [signInChanged, setSignInChanged] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      department: '',
      signIn: null // will be a number after valueAsNumber
    }
  });

  const { data: SignInData } = useGetSignInQuery({ userId, forceRefetch: true });
  const { data: user } = useGetUserQuery(userId, { forceRefetch: true });
  const [updateUserAccount, { isLoading }] = useUpdateUserAccountMutation();

  // Flatten any SignInData shape to an array of { SignInID, SignInDefinition }
  const signInOptions = (() => {
    if (!SignInData) return [];
    if (Array.isArray(SignInData)) return SignInData;
    return Object.values(SignInData).flatMap(v => (Array.isArray(v) ? v : []));
  })();

  useEffect(() => {
    if (!user) return;

    setValue('firstName', user.first_name ?? '');
    setValue('lastName', user.last_name ?? '');
    setValue('email', user.email ?? '');
    setValue('phone', user.phone ?? '');
    setValue('company', user.organization?.company ?? '');
    setValue('department', user.organization?.department ?? '');

    // Normalize various possible ID fields and coerce to number
    const signInId =
      user.signInId ??
      user.SignInId ??
      user.SignInID ??
      user.SignIn ?? // if API returns id here
      null;

    setValue(
      'signIn',
      signInId === null || signInId === undefined || signInId === ''
        ? null
        : Number(signInId) // ensure number in form state
    );
  }, [user, setValue]);

  const onSubmit = (formData) => {
    updateUserAccount({
      user_id: userId,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.phone,
      company: formData.company,
      department: formData.department,
      // This is now a Number (1 or 2) thanks to valueAsNumber
      signin: formData.signIn
    })
      .unwrap()
      .then((payload) => {
        setAlertVariant('info');
        setAlertMessage(payload.message);
        setAlertVisible(true);
      })
      .catch((error) => {
        setAlertVariant('danger');
        setAlertMessage(error?.data?.message || 'Update failed.');
        setAlertVisible(true);
      });
  };

  const signInValue = watch('signIn'); // number or null

  return (
    <>
      <h6>Account</h6>
      <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>First name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            id="firstName"
            placeholder="First name"
            {...register('firstName', { required: true })}
          />
          {errors?.firstName?.type === 'required' && (
            <p className="validate-feedback-invalid">First name is required</p>
          )}
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>Last name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            id="lastName"
            placeholder="Last name"
            {...register('lastName', { required: true })}
          />
          {errors?.lastName?.type === 'required' && (
            <p className="validate-feedback-invalid">Last name is required</p>
          )}
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control id="email" placeholder="Email address" disabled {...register('email')} />
          {errors?.email && (
            <p className="validate-feedback-invalid">Valid email is required</p>
          )}
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control id="phone" placeholder="Phone number" {...register('phone')} />
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>Company</Form.Label>
          <Form.Control id="company" placeholder="Company Name" {...register('company')} />
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>Department</Form.Label>
          <Form.Control id="department" placeholder="Department" {...register('department')} />
        </Form.Group>

        <Form.Group className="form-group col-md-6 mb-3">
          <Form.Label>User Sign-in type <span className="text-danger">*</span></Form.Label>
          <select
            id="signIn"
            className="custom-select form-select"
            // Keep value numeric (or '' while loading/empty)
            value={signInValue ?? ''}
            // Use RHF to coerce the selected value to Number automatically
            {...register('signIn', {
              required: true,
              valueAsNumber: true,
              onChange: () => setSignInChanged(true)
            })}
          >
            <option value="" disabled>
              Select a sign-in type
            </option>
            {signInOptions.map((opt) => (
              <option key={opt.SignInID} value={opt.SignInID /* number */}>
                {opt.SignInDefinition}
              </option>
            ))}
          </select>

          {errors?.signIn && (
            <small className="text-danger d-block mt-1">Sign-in type is required.</small>
          )}

          {signInChanged && (
            <small className="text-danger d-block mt-1">
              Note: Changing the sign-in type may require the user to reset their password if they
              don’t remember it.
            </small>
          )}
        </Form.Group>

        <hr className="mb-0 mt-0" />

        <div className="form-actions">
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: 5 }}
                />
                Submitting...
              </>
            ) : (
              'Update account settings'
            )}
          </Button>
        </div>
      </Form>

      <DismissibleAlert
        message={alertMessage}
        variant={alertVariant}
        show={alertVisible}
        setShow={setAlertVisible}
      />
    </>
  );
};

export default EditUserAccount;
